import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getAccessToken, doRefresh, triggerAuthFailure } from '@/lib/api-client';
import { crmKeys } from '../constants/queryKeys';

interface CrmSseEvent {
  type: string;
  entityType: string;
  entityId?: string;
  data?: Record<string, unknown>;
  timestamp: string;
}

const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] || 'http://localhost:5010/api';

/** Reconnect delay with exponential backoff (capped at 30 s) */
function getReconnectDelay(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 30_000);
}

/**
 * Parse an SSE text stream into individual events.
 *
 * The SSE spec uses double-newline delimited frames:
 *   data: {"type":"lead_updated",...}\n\n
 *
 * NestJS @Sse() sends frames as:
 *   data: <json>\n\n
 */
function parseSseEvents(chunk: string): CrmSseEvent[] {
  const events: CrmSseEvent[] = [];
  const frames = chunk.split('\n\n');

  for (const frame of frames) {
    const trimmed = frame.trim();
    if (!trimmed) continue;

    // Each frame may have multiple lines; extract data lines
    const lines = trimmed.split('\n');
    for (const line of lines) {
      if (line.startsWith('data: ') || line.startsWith('data:')) {
        const jsonStr = line.slice(line.indexOf(':') + 1).trim();
        if (!jsonStr) continue;
        try {
          events.push(JSON.parse(jsonStr) as CrmSseEvent);
        } catch {
          // Skip malformed events
        }
      }
    }
  }

  return events;
}

/**
 * Hook that connects to the CRM SSE event stream and auto-invalidates
 * React Query caches when relevant entities change.
 *
 * Uses fetch + ReadableStream instead of EventSource so that the
 * Authorization header (Bearer token) is attached. The browser's native
 * EventSource API does not support custom headers (W3C spec limitation).
 *
 * @param enabled - Whether to connect (default: true)
 */
export function useCrmEventStream(enabled = true) {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  const invalidateEntity = useCallback(
    (entityType: string) => {
      // R30-008 review: 'system' heartbeats fire every 25s (sse.service.ts).
      // Without this early-return the dashboard was refetching 144 times/hour
      // per open tab even with no real CRM activity. The case below also
      // breaks for 'system', but the unconditional invalidate above
      // contradicted that no-op.
      if (entityType === 'system') return;

      // Always invalidate dashboard on any entity change
      queryClient.invalidateQueries({ queryKey: crmKeys.dashboard });

      switch (entityType) {
        case 'lead':
          queryClient.invalidateQueries({ queryKey: crmKeys.leads });
          break;
        case 'deal':
          queryClient.invalidateQueries({ queryKey: crmKeys.deals });
          break;
        case 'account':
          queryClient.invalidateQueries({ queryKey: crmKeys.accounts });
          break;
        case 'contact':
          queryClient.invalidateQueries({ queryKey: crmKeys.contacts });
          break;
        case 'task':
          queryClient.invalidateQueries({ queryKey: crmKeys.tasks });
          break;
        case 'case':
          queryClient.invalidateQueries({ queryKey: crmKeys.cases });
          break;
        case 'quote':
          queryClient.invalidateQueries({ queryKey: crmKeys.quotes });
          break;
        case 'invoice':
          queryClient.invalidateQueries({ queryKey: crmKeys.invoices });
          break;
        case 'salesOrder':
          queryClient.invalidateQueries({ queryKey: crmKeys.salesOrders });
          break;
        case 'campaign':
          queryClient.invalidateQueries({ queryKey: crmKeys.campaigns });
          break;
        case 'vendor':
          queryClient.invalidateQueries({ queryKey: crmKeys.vendors });
          break;
        case 'contract':
          queryClient.invalidateQueries({ queryKey: crmKeys.contracts });
          break;
        case 'forecast':
          queryClient.invalidateQueries({ queryKey: crmKeys.forecasts });
          break;
        case 'siteVisit':
          queryClient.invalidateQueries({ queryKey: crmKeys.siteVisits });
          break;
        case 'creditNote':
          queryClient.invalidateQueries({ queryKey: crmKeys.creditNotes });
          break;
        case 'payment':
          queryClient.invalidateQueries({ queryKey: crmKeys.payments });
          break;
        case 'activity':
          // R30-008: activities was missing from the dispatch table so the
          // CRM Overview dashboard never invalidated on activity events.
          // The unconditional crmKeys.dashboard invalidate above also fires —
          // this case is what keeps the Activities list in sync cross-tab.
          queryClient.invalidateQueries({ queryKey: crmKeys.activities });
          break;
        case 'settings':
          queryClient.invalidateQueries({ queryKey: crmKeys.settings });
          break;
        case 'system':
          // Heartbeat — no invalidation needed
          break;
      }
    },
    [queryClient],
  );

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let reconnectAttempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      if (cancelled) return;

      // Always fetch a fresh token before each connect attempt
      const token = getAccessToken();
      const tenantId = localStorage.getItem('tenant_id');
      if (!token || !tenantId) {
        // Not authenticated yet — retry after a short delay
        reconnectTimer = setTimeout(() => {
          if (!cancelled) void connect();
        }, 2000);
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await fetch(`${API_BASE_URL}/crm/sse/events`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
            'X-Tenant-Id': tenantId,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          // On 401/403, attempt a token refresh before reconnecting
          if (response.status === 401 || response.status === 403) {
            try {
              await doRefresh();
              // Refresh succeeded — reconnect with the new token after a brief delay
              setTimeout(() => {
                if (!cancelled) void connect();
              }, 500);
            } catch {
              // Refresh failed — session is invalid, trigger proper logout
              triggerAuthFailure();
            }
            return;
          }
          throw new Error(`SSE connect failed: ${response.status}`);
        }

        // Reset backoff on successful connection
        reconnectAttempt = 0;

        const reader = response.body?.getReader();
        if (!reader) return;

        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process complete frames (delimited by \n\n)
          if (buffer.includes('\n\n')) {
            const events = parseSseEvents(buffer);
            // Keep any trailing incomplete frame
            const lastDoubleNewline = buffer.lastIndexOf('\n\n');
            buffer = buffer.slice(lastDoubleNewline + 2);

            for (const event of events) {
              invalidateEntity(event.entityType);
            }
          }
        }
      } catch (err) {
        if (cancelled) return;
        // AbortError is expected on cleanup
        if (err instanceof DOMException && err.name === 'AbortError') return;
      }

      // Auto-reconnect with exponential backoff
      if (!cancelled) {
        const delay = getReconnectDelay(reconnectAttempt++);
        reconnectTimer = setTimeout(connect, delay);
      }
    };

    // Delay initial connect to let auth context settle (login, token storage, React StrictMode)
    const initialTimer = setTimeout(connect, 1500);

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      abortRef.current?.abort();
      abortRef.current = null;
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [enabled, invalidateEntity]);
}
